import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { clearToken, fetchMe, login as apiLogin, setToken, googleExchangeSession, apiLogout } from "@/lib/api";

const AuthContext = createContext(null);

/**
 * Auth states:
 *   - user === undefined  → still checking (loading)
 *   - user === null       → not authenticated
 *   - user object         → authenticated admin
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined);

  const bootstrap = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      // stay in "loading" — AuthCallback will call setUser once it's done
      return;
    }
    // Server is the source of truth: always ask /auth/me. It accepts either the
    // localStorage JWT (Authorization header) or the httpOnly session cookie
    // set by Emergent Google sign-in — the cookie is invisible to JS, so the
    // localStorage check alone is not enough.
    try {
      const me = await fetchMe();
      setUser(me);
    } catch {
      clearToken();
      setUser(null);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const signIn = useCallback(async (email, password) => {
    const { token, user: u } = await apiLogin(email, password);
    setToken(token);
    setUser(u);
    return u;
  }, []);

  const signInWithGoogleSession = useCallback(async (sessionId) => {
    const { token, user: u } = await googleExchangeSession(sessionId);
    setToken(token);
    setUser(u);
    return u;
  }, []);

  const signOut = useCallback(async () => {
    try { await apiLogout(); } catch { /* non-fatal */ }
    clearToken();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, signIn, signInWithGoogleSession, signOut, refresh: bootstrap }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
